import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../context/CartContext';
import { formatPrice, getEstimatedDelivery } from '../utils/formatPrice';
import { useMarket } from '../context/MarketContext';
import { useAuth } from '../context/AuthContext';
import QuantitySelector from '../components/common/QuantitySelector';
import { calculateLoyaltyDiscount } from '../api/real/loyaltyApi';
import { OfferItemTag } from '../components/cart/OfferNotice';

const CartPage = () => {
  const {
    items, subtotal, removeItem, updateQuantity, clearCart,
    appliedCoupon, couponDiscount, couponError, couponLoading,
    applyCoupon: ctxApplyCoupon, removeCoupon: ctxRemoveCoupon,
    openCheckout, offer, offerDiscount, offerForItem, recheckCart,
  } = useCart();
  // prices and stock may have changed since things were added: check once when the page opens
  useEffect(() => { recheckCart(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [couponInput, setCouponInput] = useState('');
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);
  const { currentMarket } = useMarket();
  const { user } = useAuth();

  const fmtPrice = useCallback((amount) => {
    const symbol = currentMarket?.currencySymbol;
    if (symbol && symbol !== '₹') {
      return `${symbol}${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return formatPrice(amount);
  }, [currentMarket]);

  // Global fallbacks
  const globalThreshold = currentMarket?.freeShippingThreshold ?? 699;
  const globalFee       = currentMarket?.shippingBaseFee ?? 80;

  // Prepaid-specific values (shown in cart — prepaid is the default/preferred method)
  const prepaidFee       = currentMarket?.prepaidShippingFee != null ? Number(currentMarket.prepaidShippingFee) : globalFee;
  const prepaidThreshold = currentMarket?.prepaidFreeShippingThreshold != null ? Number(currentMarket.prepaidFreeShippingThreshold) : globalThreshold;

  // like the server: a free item does not count towards free shipping or the prepaid discount
  const pricedSubtotal = Math.max(0, subtotal - offerDiscount);
  const shipping  = pricedSubtotal >= prepaidThreshold ? 0 : prepaidFee;
  const discount  = couponDiscount;
  const total     = Math.max(0, subtotal - offerDiscount + shipping - discount);
  const freeShipLeft = Math.max(0, prepaidThreshold - pricedSubtotal);
  const freeShipPct  = Math.min(100, (pricedSubtotal / prepaidThreshold) * 100);


  // Prepaid discount preview — shown in the cart banner
  const prepaidDiscountPreview = (() => {
    if (!currentMarket) return 0;
    const percent  = currentMarket.prepaidDiscountPercent;
    const flat     = currentMarket.prepaidDiscountFlat;
    const applyMax = currentMarket.prepaidApplyMaximum;
    const minOV    = currentMarket.prepaidMinOrderValue;
    const maxOV    = currentMarket.prepaidMaxOrderValue;
    if ((!percent || Number(percent) === 0) && (!flat || Number(flat) === 0)) return 0;
    if (minOV != null && pricedSubtotal < Number(minOV)) return 0;
    if (maxOV != null && pricedSubtotal > Number(maxOV)) return 0;
    const pctDiscount = percent ? Math.round((pricedSubtotal * Number(percent) / 100) * 100) / 100 : 0;
    if (!flat || Number(flat) === 0) return Math.max(0, pctDiscount);
    const flatNum = Number(flat);
    return Math.max(0, applyMax ? Math.max(pctDiscount, flatNum) : Math.min(pctDiscount, flatNum));
  })();

  useEffect(() => {
    if (subtotal <= 0) return;
    calculateLoyaltyDiscount(subtotal)
      .then(data => setLoyaltyInfo(data))
      .catch(() => {});
  }, [subtotal]);

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    const ok = await ctxApplyCoupon(couponInput, {
      cartSubtotal: subtotal,
      marketCode: currentMarket?.isDefault ? null : (currentMarket?.code ?? null),
    });
    if (ok) setCouponInput('');
  };

  const removeCoupon = () => {
    ctxRemoveCoupon();
    setCouponInput('');
  };

  if (items.length === 0) {
    return (
      <>
        <Helmet><title>Shopping Cart — Skinn Thrive</title></Helmet>
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <div className="w-24 h-24 rounded-full bg-primary-light flex items-center justify-center mx-auto mb-5">
            <svg className="w-10 h-10 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 10H4L5 9z"/>
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-ink mb-2">Your cart is empty</h1>
          <p className="text-ink-soft mb-7">Discover derma-approved skincare products for your skin!</p>
          <Link to="/shop" className="btn-primary btn-lg">Start Shopping →</Link>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet><title>Shopping Cart — Skinn Thrive</title></Helmet>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-ink tracking-tight">
            Shopping Cart
            <span className="text-ink-muted font-normal text-base ml-2">({items.length} item{items.length !== 1 ? 's' : ''})</span>
          </h1>
          <button onClick={clearCart} className="text-xs text-red-400 hover:text-red-600 font-medium transition-colors">
            Clear cart
          </button>
        </div>

        {/* Free shipping progress */}
        <div className={`rounded-xl p-3.5 flex items-center gap-3 ${prepaidDiscountPreview > 0 ? 'mb-3' : 'mb-6'} ${freeShipLeft === 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
          <span className="text-xl shrink-0">{freeShipLeft === 0 ? '🎉' : '🚚'}</span>
          <div className="flex-1">
            {freeShipLeft === 0 ? (
              <p className="text-sm font-semibold text-emerald-700">You've unlocked FREE shipping!</p>
            ) : (
              <>
                <p className="text-sm font-medium text-amber-800">Add <span className="font-bold">{fmtPrice(freeShipLeft)}</span> more for FREE shipping</p>
                <div className="h-1.5 bg-amber-200 rounded-full mt-1.5 overflow-hidden">
                  <div className="h-full bg-amber-500 rounded-full transition-all duration-500" style={{ width: `${freeShipPct}%` }} />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Prepaid discount banner */}
        {prepaidDiscountPreview > 0 && (
          <div className="rounded-xl p-3.5 mb-6 flex items-center gap-3 bg-purple-50 border border-purple-200">
            <span className="text-xl shrink-0">💳</span>
            <p className="text-sm font-medium text-purple-800">
              Save <span className="font-bold text-purple-700">{fmtPrice(prepaidDiscountPreview)}</span> by paying online (prepaid)!
            </p>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Items list — 60% */}
          <div className="flex-1 space-y-3">
            {items.map((item, idx) => (
              <div
                key={`${item.id}-${item.selectedVariant}-${idx}`}
                className="bg-white rounded-2xl border border-border p-4 flex gap-3 sm:gap-4"
                style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
              >
                <Link to={`/product/${item.handle}`} className="shrink-0">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden bg-surface border border-border">
                    <img src={item.images[0]} alt={item.title} className="w-full h-full object-contain p-1" />
                  </div>
                </Link>
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-primary">{item.vendor}</span>
                  <Link to={`/product/${item.handle}`}>
                    <h3 className="font-medium text-ink hover:text-primary transition-colors line-clamp-2 text-sm mt-0.5">{item.title}</h3>
                  </Link>
                  {item.selectedVariant && item.selectedVariant !== 'default' && (
                    <p className="text-xs text-ink-muted mt-0.5">{item.selectedVariant}</p>
                  )}
                  <OfferItemTag line={offerForItem(item)} unitPrice={item.price} fmt={fmtPrice} />
                  <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                    <QuantitySelector
                      value={item.quantity}
                      onChange={q => updateQuantity(item.id, item.selectedVariant, q)}
                      min={0}
                    />
                    <div className="text-right">
                      <p className="font-bold text-ink">{fmtPrice(item.price * item.quantity)}</p>
                      {item.quantity > 1 && (
                        <p className="text-xs text-ink-muted">{fmtPrice(item.price)} each</p>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => removeItem(item.id, item.selectedVariant)}
                  className="shrink-0 self-start p-1.5 text-ink-muted hover:text-red-400 hover:bg-red-50 rounded-lg transition-all"
                  aria-label="Remove item"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </div>
            ))}

            {/* Continue shopping */}
            <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:underline mt-2">
              ← Continue Shopping
            </Link>
          </div>

          {/* Order summary — 40% */}
          <div className="lg:w-80 xl:w-96 shrink-0">
            <div
              className="bg-white rounded-2xl border border-border p-5 sticky top-24"
              style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(107,33,168,0.06)' }}
            >
              <h2 className="font-bold text-ink text-base mb-4">Order Summary</h2>

              {/* Promo code */}
              <div className="mb-5">
                {appliedCoupon ? (
                  <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5">
                    <div>
                      <p className="text-xs font-bold text-emerald-700 uppercase tracking-wide">{appliedCoupon.code} applied</p>
                      <p className="text-xs text-emerald-600 mt-0.5">{appliedCoupon.message}</p>
                    </div>
                    <button onClick={removeCoupon} className="text-emerald-600 hover:text-red-500 transition-colors ml-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponInput}
                      onChange={e => setCouponInput(e.target.value.toUpperCase())}
                      onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()}
                      placeholder="Promo code"
                      className="input-field flex-1 text-sm py-2.5 uppercase placeholder:normal-case"
                      disabled={couponLoading}
                    />
                    <button
                      onClick={handleApplyCoupon}
                      disabled={couponLoading || !couponInput.trim()}
                      className="btn-outline px-4 py-2.5 text-sm font-semibold shrink-0 disabled:opacity-50"
                    >
                      {couponLoading ? '...' : 'Apply'}
                    </button>
                  </div>
                )}
                {couponError && !appliedCoupon && (
                  <p className="text-xs text-red-500 mt-1.5 pl-1">{couponError}</p>
                )}
              </div>

              {/* Price breakdown */}
              <div className="space-y-2.5 text-sm border-t border-border pt-4 mb-4">
                <div className="flex justify-between text-ink-soft">
                  <span>Subtotal ({items.reduce((s, i) => s + i.quantity, 0)} items)</span>
                  <span className="font-medium text-ink">{fmtPrice(subtotal)}</span>
                </div>
                {offerDiscount > 0 && (
                  <div className="flex justify-between text-emerald-600">
                    <span>Offer · {offer.title}</span>
                    <span className="font-medium">−{fmtPrice(offerDiscount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-ink-soft">
                  <span>Shipping</span>
                  <span className={`font-medium ${shipping === 0 ? 'text-emerald-600' : 'text-ink'}`}>
                    {shipping === 0 ? 'FREE' : fmtPrice(shipping)}
                  </span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-emerald-600">
                    <span>Promo discount</span>
                    <span className="font-medium">−{fmtPrice(discount)}</span>
                  </div>
                )}
                <div className="border-t border-border pt-2.5 flex justify-between font-bold text-base text-ink">
                  <span>Total</span>
                  <span className="text-primary">{fmtPrice(total)}</span>
                </div>
              </div>

              {/* Estimated delivery */}
              <p className="text-xs text-ink-soft mb-4 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 text-emerald-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
                Estimated delivery: <span className="font-semibold text-ink">{getEstimatedDelivery('prepaid')}</span>
              </p>

              {/* Loyalty earn preview */}
              {(() => {
                if (!loyaltyInfo?.enabled || !loyaltyInfo?.earnEnabled) return null;
                const earnRate = Number(loyaltyInfo.earnRate || 1);
                const maxEarn  = Number(loyaltyInfo.maxEarnPerOrder || 500);
                const pts = Math.min(Math.floor(total / 10) * earnRate, maxEarn);
                if (pts <= 0) return null;
                return (
                  <div className="rounded-xl border border-purple-200 overflow-hidden mb-4">
                    <div className="flex items-center gap-2 px-3.5 py-2.5"
                      style={{ background: 'linear-gradient(135deg, #5B21B6, #7C3AED)' }}>
                      <svg className="w-4 h-4 text-yellow-300 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
                      </svg>
                      <p className="text-xs font-bold text-white">
                        {user ? "You'll earn reward points!" : "Login & earn reward points!"}
                      </p>
                    </div>
                    <div className="bg-purple-50 px-3.5 py-2.5 flex items-center justify-between">
                      <p className="text-xs text-purple-700">Points from this order</p>
                      <div className="text-right">
                        <p className="text-sm font-bold text-purple-800">+{pts} pts</p>
                        {loyaltyInfo.rupeesPerPoint > 0 && (
                          <p className="text-[11px] text-purple-500">
                            ≈ ₹{(pts * Number(loyaltyInfo.rupeesPerPoint)).toFixed(0)} value
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              <button
                onClick={openCheckout}
                className="btn-primary w-full py-3.5 flex items-center justify-center gap-2 mb-3"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                </svg>
                Secure Checkout
              </button>

              {/* Trust row */}
              <div className="flex items-center justify-center gap-4 text-xs text-ink-muted">
                <span className="flex items-center gap-1">🔒 SSL Secure</span>
                <span className="flex items-center gap-1">✅ 100% Authentic</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CartPage;
